import { useMemo } from "react";
import { buildLegacyOpsNav } from "@/app/nav/buildLegacyOpsNav";
import { getNavFlags, recordNavFallback } from "@/app/nav/navFlags";
import { resolveApiNav } from "@/app/nav/resolveApiNav";
import { getScreenById } from "@/app/screenRegistry";
import type { NavSource, OpsNavGroup } from "@/app/nav/types";
import { useNavQuery } from "@/modules/sys/queries/useNavQuery";
import { useAuth } from "@/shared/auth/AuthContext";

export type OpsNavState = {
  groups: OpsNavGroup[];
  sourceUsed: NavSource;
  isLoading: boolean;
  /** Shown when API source fails and fallback is off (W5 hard cut). */
  errorBanner: string | null;
  profileVersion: number | null;
};

/** Ops sidebar — API SoT; legacy only via VITE_NAV_SOURCE=legacy. */
export function useOpsNav(): OpsNavState {
  const { user } = useAuth();
  const flags = useMemo(() => getNavFlags(), []);
  const permissions = user?.permissions ?? [];

  const legacy = useMemo(
    () => buildLegacyOpsNav(permissions),
    [permissions],
  );

  const apiEnabled = flags.source === "api";
  const apiQuery = useNavQuery("ops", apiEnabled);

  const productMenuLabel = getScreenById("SCR-ENG-PRODUCT")?.label;
  const productTypeMenuLabel = getScreenById("SCR-ENG-PRODUCT-TYPE")?.label;
  const apiGroups = useMemo(
    () => (apiQuery.data ? resolveApiNav(apiQuery.data) : null),
    [apiQuery.data, productMenuLabel, productTypeMenuLabel],
  );

  return useMemo((): OpsNavState => {
    if (flags.source === "legacy") {
      recordNavFallback("ops_source_legacy");
      return {
        groups: legacy,
        sourceUsed: "legacy",
        isLoading: false,
        errorBanner: null,
        profileVersion: null,
      };
    }

    if (apiGroups) {
      return {
        groups: apiGroups,
        sourceUsed: "api",
        isLoading: false,
        errorBanner: null,
        profileVersion: apiQuery.data?.profileVersion ?? null,
      };
    }

    if (apiQuery.isLoading || apiQuery.isFetching) {
      return {
        groups: [],
        sourceUsed: "api",
        isLoading: true,
        errorBanner: null,
        profileVersion: null,
      };
    }

    if (apiQuery.isError) {
      if (flags.fallback) {
        recordNavFallback("ops_api_error");
        return {
          groups: legacy,
          sourceUsed: "legacy",
          isLoading: false,
          errorBanner: null,
          profileVersion: null,
        };
      }
      return {
        groups: [],
        sourceUsed: "api",
        isLoading: false,
        errorBanner: "Không tải được menu. Thử lại.",
        profileVersion: null,
      };
    }

    return {
      groups: [],
      sourceUsed: "api",
      isLoading: false,
      errorBanner: null,
      profileVersion: null,
    };
  }, [
    flags.source,
    flags.fallback,
    apiGroups,
    apiQuery.data?.profileVersion,
    apiQuery.isLoading,
    apiQuery.isFetching,
    apiQuery.isError,
    legacy,
  ]);
}
